/* ------------------------------------------------------------------
  File: Alert.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable alert component for the Accend Holdings website.
//   - Supports visible feedback messages, variants, optional icons,
//     optional title, action area, dismissible state, and dark-surface support.
//   - Suitable for success messages, errors, warnings, information notices,
//     form feedback, and system messages.

/**
 * Alert
 *
 * A reusable visible feedback/message component.
 *
 * @param {"info" | "success" | "warning" | "error" | "neutral" | "inverse"} [variant="info"]
 * Controls the alert visual style.
 *
 * @param {string} [title]
 * Optional alert title.
 *
 * @param {React.ReactNode} children
 * Main alert message/content.
 *
 * @param {React.ReactNode} [icon]
 * Optional custom icon. If not provided, a default icon is used.
 *
 * @param {React.ReactNode} [action]
 * Optional action element such as a Button or link.
 *
 * @param {boolean} [dismissible=false]
 * Controls whether the dismiss button is shown.
 *
 * @param {Function} [onDismiss]
 * Function called when the dismiss button is clicked.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import {
  LuCircleAlert,
  LuCircleCheck,
  LuCircleHelp,
  LuInfo,
  LuTriangleAlert,
  LuX,
} from "react-icons/lu";
import "./Alert.css";

const defaultIcons = {
  info: <LuInfo />,
  success: <LuCircleCheck />,
  warning: <LuTriangleAlert />,
  error: <LuCircleAlert />,
  neutral: <LuCircleHelp />,
  inverse: <LuInfo />,
};

const Alert = ({
  variant = "info",
  title,
  children,
  icon,
  action,
  dismissible = false,
  onDismiss,
  className = "",
  ...props
}) => {
  const alertClasses = [
    "acc-alert",
    `acc-alert--${variant}`,
    action ? "acc-alert--with-action" : "",
    dismissible ? "acc-alert--dismissible" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const alertIcon = icon || defaultIcons[variant] || defaultIcons.info;

  return (
    <div className={alertClasses} role="status" {...props}>
      {alertIcon && (
        <span className="acc-alert__icon" aria-hidden="true">
          {alertIcon}
        </span>
      )}

      <div className="acc-alert__content">
        {title && <h3 className="acc-alert__title">{title}</h3>}

        {children && <div className="acc-alert__message">{children}</div>}
      </div>

      {action && <div className="acc-alert__action">{action}</div>}

      {dismissible && (
        <button
          type="button"
          className="acc-alert__dismiss"
          aria-label="Dismiss alert"
          onClick={onDismiss}
        >
          <LuX />
        </button>
      )}
    </div>
  );
};

export default Alert;